import { useCallback, useMemo, useState } from 'react'
import {
  countActiveFilters,
  createClearedPhotosPanelFilters,
} from '@/lib/photos/count-active-filters'
import type { PhotosFilterTriState } from '@/lib/photos/types/photos-filter-tri-state'
import type { PhotosOrientationFilter } from '@/lib/photos/types/photos-orientation-filter'
import { buildTrayFilters } from '@/lib/photos/filter-photos'
import { isFilenameSearchActive } from '@/lib/photos/filename-search'

/**
 * Everything inside the Photos tray's Filters section: topics, students, filename search and the
 * Orientation / Placed / Tagged / Favorited rows, plus the badge count and Clear filters. The
 * album selector sits outside the section and is never touched by Clear filters.
 */
export function usePhotosTrayFilters() {
  const [selectedTopicTagIds, setSelectedTopicTagIds] = useState<string[]>([])
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([])
  const [filenameQuery, setFilenameQuery] = useState('')
  const [placedFilter, setPlacedFilter] = useState<PhotosFilterTriState>('any')
  const [taggedFilter, setTaggedFilter] = useState<PhotosFilterTriState>('any')
  const [favoritedFilter, setFavoritedFilter] = useState<PhotosFilterTriState>('any')
  const [orientationFilter, setOrientationFilter] = useState<PhotosOrientationFilter>('any')

  const isFilenameSearch = isFilenameSearchActive(filenameQuery)

  const trayFilters = useMemo(
    () =>
      buildTrayFilters({
        isFilenameSearchActive: isFilenameSearch,
        topicTagIds: selectedTopicTagIds,
        studentIds: selectedStudentIds,
        placed: placedFilter,
        tagged: taggedFilter,
        favorited: favoritedFilter,
        orientation: orientationFilter,
      }),
    [
      favoritedFilter,
      isFilenameSearch,
      orientationFilter,
      placedFilter,
      selectedStudentIds,
      selectedTopicTagIds,
      taggedFilter,
    ]
  )

  const activeFilterCount = countActiveFilters({
    topicTagIds: selectedTopicTagIds,
    studentIds: selectedStudentIds,
    filenameQuery,
    placed: placedFilter,
    tagged: taggedFilter,
    favorited: favoritedFilter,
    orientation: orientationFilter,
  })

  const handleClearFilters = useCallback(() => {
    const cleared = createClearedPhotosPanelFilters()

    setSelectedTopicTagIds(cleared.topicTagIds)
    setSelectedStudentIds(cleared.studentIds)
    setFilenameQuery(cleared.filenameQuery)
    setPlacedFilter(cleared.placed)
    setTaggedFilter(cleared.tagged)
    setFavoritedFilter(cleared.favorited)
    setOrientationFilter(cleared.orientation)
  }, [])

  return {
    selectedTopicTagIds,
    setSelectedTopicTagIds,
    selectedStudentIds,
    setSelectedStudentIds,
    filenameQuery,
    setFilenameQuery,
    placedFilter,
    setPlacedFilter,
    taggedFilter,
    setTaggedFilter,
    favoritedFilter,
    setFavoritedFilter,
    orientationFilter,
    setOrientationFilter,
    isFilenameSearch,
    trayFilters,
    activeFilterCount,
    handleClearFilters,
  }
}

export type PhotosTrayFilters = ReturnType<typeof usePhotosTrayFilters>
